import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import { useGenerateDishImages, useEnhanceDishImage, useSaveDishImage } from '../../../../queries/dishImages';
import { useGetWallMediaLibrary } from '../../../../queries/videoWall';
import { useCustomToast } from '../../../Contexts/ToastContext';

/**
 * AI Dish Photos dialog — two modes:
 *  • Generate: type a dish name (+ style) → Nano Banana returns 3 studio options
 *    → pick one → saved to the media library.
 *  • Enhance: upload/snap a real photo (+ style) → AI returns a pro version
 *    → before/after → save.
 * On save, calls onSaved({ media_id, path, thumb }); the parent sets the menu
 * item's photo_media_id and refreshes the media library. Nothing is stored until
 * the user picks (generate/enhance only return previews + short-lived tokens).
 */
const STYLES = ['bright_clean', 'overhead_flatlay', 'dark_moody', 'close_up', 'table_setting', 'cutout'];
const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 15 * 1024 * 1024;
const dataUrl = (img) => `data:${img.mime};base64,${img.b64}`;

export default function DishImageDialog({ visible, onHide, initialDishName = '', onSaved }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const gen = useGenerateDishImages();
  const enh = useEnhanceDishImage();
  const save = useSaveDishImage();

  const [mode, setMode] = useState('generate');
  const [style, setStyle] = useState('bright_clean');
  const [error, setError] = useState(null);

  // generate
  const [dishName, setDishName] = useState(initialDishName);
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null); // token

  // enhance — source is either an uploaded file or a picked library image
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [file, setFile] = useState(null);
  const [origUrl, setOrigUrl] = useState(null);
  const [libMedia, setLibMedia] = useState(null); // { id, path, thumb } from the library
  const [libOpen, setLibOpen] = useState(false);   // showing the library grid
  const [instruction, setInstruction] = useState(''); // optional free-text request
  const [enhanced, setEnhanced] = useState(null); // { token, mime, b64 }

  const { data: mediaRaw } = useGetWallMediaLibrary(visible);
  const images = useMemo(() => {
    const arr = mediaRaw?.data ?? mediaRaw ?? [];
    return (Array.isArray(arr) ? arr : []).filter((m) => m.type === 'IMAGE');
  }, [mediaRaw]);

  const hasSource = !!file || !!libMedia;
  const beforeSrc = file ? origUrl : (libMedia?.path || libMedia?.thumb || null);

  useEffect(() => { if (visible) setDishName(initialDishName); }, [visible, initialDishName]);

  const reset = () => {
    setError(null); setOptions([]); setPicked(null);
    setEnhanced(null); setFile(null); setLibMedia(null); setLibOpen(false);
    setInstruction('');
    if (origUrl) URL.revokeObjectURL(origUrl);
    setOrigUrl(null);
  };
  const close = () => { reset(); onHide(); };
  const switchMode = (m) => { reset(); setMode(m); };

  /* ── generate ── */
  const doGenerate = async () => {
    if (!dishName.trim()) return;
    setError(null); setOptions([]); setPicked(null);
    try {
      const res = await gen.mutateAsync({ dish_name: dishName.trim(), style, count: 3, aspect: '1:1' });
      if (res?.status === 422 || !res?.options?.length) {
        setError(res?.msg || t('video_wall.dish_ai.gen_failed'));
        return;
      }
      setOptions(res.options);
    } catch { setError(t('video_wall.dish_ai.gen_failed')); }
  };

  /* ── enhance ── */
  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setError(null); setEnhanced(null); setLibMedia(null);
    if (f.size > MAX_BYTES) { setError(t('video_wall.dish_ai.too_large')); return; }
    setFile(f);
    if (origUrl) URL.revokeObjectURL(origUrl);
    setOrigUrl(URL.createObjectURL(f));
  };
  const pickFromLibrary = (m) => {
    setError(null); setEnhanced(null);
    setFile(null);
    if (origUrl) URL.revokeObjectURL(origUrl);
    setOrigUrl(null);
    setLibMedia({ id: m.id, path: m.path, thumb: m.thumb });
    setLibOpen(false);
  };
  const doEnhance = async () => {
    if (!hasSource) return;
    setError(null); setEnhanced(null);
    try {
      const instr = instruction.trim() || undefined;
      const res = await enh.mutateAsync(file
        ? { file, style, instruction: instr }
        : { mediaId: libMedia.id, style, instruction: instr });
      if (res?.status === 422 || !res?.enhanced) {
        setError(res?.msg || t('video_wall.dish_ai.enhance_failed'));
        return;
      }
      setEnhanced(res.enhanced);
    } catch { setError(t('video_wall.dish_ai.enhance_failed')); }
  };

  /* ── save chosen ── */
  const persist = async (token, name) => {
    try {
      const media = await save.mutateAsync({ token, name: (name || '').trim() || undefined });
      if (!media?.media_id) { setError(t('video_wall.dish_ai.save_failed')); return; }
      showToast('success', t('video_wall.dish_ai.title'), t('video_wall.dish_ai.saved_toast'));
      onSaved({ media_id: media.media_id, path: media.path, thumb: media.thumb });
      close();
    } catch { setError(t('video_wall.dish_ai.save_failed')); }
  };

  const busy = gen.isLoading || enh.isLoading || save.isLoading;

  const StyleChips = () => (
    <div className="d-flex flex-wrap gap-2">
      {STYLES.map((s) => (
        <Button key={s} size="small" label={t(`video_wall.dish_ai.style_${s}`)}
          outlined={style !== s} onClick={() => setStyle(s)} disabled={busy} />
      ))}
    </div>
  );

  return (
    <Dialog visible={visible} onHide={close} style={{ width: 'min(96vw, 680px)' }}
      header={(
        <span><i className="pi pi-sparkles me-2" style={{ color: 'var(--pp-primary)' }} />
          {t('video_wall.dish_ai.title')}</span>
      )}>
      {/* hidden inputs */}
      <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} hidden />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPick} hidden />

      {/* mode switch */}
      <div className="d-flex gap-2 mb-3">
        <Button className="flex-fill" icon="pi pi-image" label={t('video_wall.dish_ai.mode_generate')}
          outlined={mode !== 'generate'} onClick={() => switchMode('generate')} disabled={busy} />
        <Button className="flex-fill" icon="pi pi-bolt" label={t('video_wall.dish_ai.mode_enhance')}
          outlined={mode !== 'enhance'} onClick={() => switchMode('enhance')} disabled={busy} />
      </div>

      {error && <Message severity="error" text={error} className="w-100 mb-3" />}

      {/* ───────── GENERATE ───────── */}
      {mode === 'generate' && (
        <div className="d-flex flex-column gap-3">
          <div>
            <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.dish_name')}</label>
            <InputText className="w-100" value={dishName} maxLength={200} disabled={busy}
              onChange={(e) => setDishName(e.target.value)} placeholder={t('video_wall.dish_ai.dish_name_ph')} />
          </div>
          <div>
            <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.style')}</label>
            <StyleChips />
          </div>

          {gen.isLoading ? (
            <div className="text-center py-5">
              <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
              <h6 className="mt-3 mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.dish_ai.generating')}</h6>
              <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.generating_hint')}</p>
            </div>
          ) : options.length ? (
            <>
              <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.pick_hint')}</p>
              <div className="row g-2">
                {options.map((o) => (
                  <div key={o.token} className="col-4">
                    <button type="button" className="p-0 w-100" style={{ background: 'transparent', cursor: 'pointer',
                      border: `3px solid ${picked === o.token ? 'var(--pp-btn-honey)' : 'transparent'}`, borderRadius: 12 }}
                      onClick={() => setPicked(o.token)}>
                      <img src={dataUrl(o)} alt="" className="w-100" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 9 }} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="d-flex justify-content-between gap-2">
                <Button text icon="pi pi-refresh" label={t('video_wall.dish_ai.regenerate')} onClick={doGenerate} disabled={busy} />
                <Button icon="pi pi-check" label={t('video_wall.dish_ai.use_photo')}
                  disabled={!picked || busy} loading={save.isLoading} onClick={() => persist(picked, dishName)} />
              </div>
            </>
          ) : (
            <Button icon="pi pi-sparkles" label={t('video_wall.dish_ai.generate')}
              disabled={!dishName.trim() || busy} onClick={doGenerate} />
          )}
        </div>
      )}

      {/* ───────── ENHANCE ───────── */}
      {mode === 'enhance' && (
        <div className="d-flex flex-column gap-3">
          {libOpen ? (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center justify-content-between">
                <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.pick_from_library')}</span>
                <Button text icon="pi pi-arrow-left" label={t('video_wall.dish_ai.change')} onClick={() => setLibOpen(false)} />
              </div>
              {images.length === 0 ? (
                <p className="small text-center py-4" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.no_library_images')}</p>
              ) : (
                <div className="row g-2" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {images.map((m) => (
                    <div key={m.id} className="col-4 col-md-3">
                      <button type="button" className="p-0 w-100" style={{ background: 'transparent', cursor: 'pointer', border: 'none', borderRadius: 9 }}
                        onClick={() => pickFromLibrary(m)}>
                        <img src={m.thumb && m.thumb !== 'loading' ? m.thumb : m.path} alt={m.file_name}
                          className="w-100" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 9, border: '1px solid var(--pp-border)' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : !hasSource ? (
            <div className="d-flex flex-column gap-2">
              <div className="d-flex gap-2">
                <Button className="flex-fill" outlined icon="pi pi-folder-open"
                  label={t('video_wall.dish_ai.choose_file')} onClick={() => fileRef.current?.click()} />
                <Button className="flex-fill" outlined icon="pi pi-camera"
                  label={t('video_wall.dish_ai.take_photo')} onClick={() => cameraRef.current?.click()} />
              </div>
              <Button outlined icon="pi pi-images"
                label={t('video_wall.dish_ai.choose_library')} onClick={() => setLibOpen(true)} />
            </div>
          ) : (
            <>
              <div>
                <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.style')}</label>
                <StyleChips />
              </div>
              <div>
                <label className="d-block mb-1 small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.instruction_label')}</label>
                <InputTextarea className="w-100" rows={2} autoResize value={instruction} maxLength={300} disabled={busy}
                  onChange={(e) => setInstruction(e.target.value)} dir="auto"
                  placeholder={t('video_wall.dish_ai.instruction_ph')} />
              </div>

              {enh.isLoading ? (
                <div className="text-center py-5">
                  <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                  <h6 className="mt-3 mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.dish_ai.enhancing')}</h6>
                  <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.generating_hint')}</p>
                </div>
              ) : (
                <div className="row g-2 align-items-center">
                  <div className="col-6 text-center">
                    <div className="small mb-1" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.before')}</div>
                    <img src={beforeSrc} alt="" className="w-100" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 9, opacity: enhanced ? 0.85 : 1 }} />
                  </div>
                  <div className="col-6 text-center">
                    <div className="small mb-1" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.dish_ai.after')}</div>
                    {enhanced ? (
                      <img src={dataUrl(enhanced)} alt="" className="w-100" style={{ aspectRatio: '1', objectFit: 'cover', borderRadius: 9, border: '3px solid var(--pp-primary)' }} />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center" style={{ aspectRatio: '1', borderRadius: 9, background: 'var(--pp-bg-secondary)', border: '1px dashed var(--pp-border-strong)' }}>
                        <i className="pi pi-sparkles" style={{ color: 'var(--pp-text-secondary)' }} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between gap-2">
                <Button text icon="pi pi-times" label={t('video_wall.dish_ai.change')} onClick={reset} disabled={busy} />
                {enhanced ? (
                  <div className="d-flex gap-2">
                    <Button text icon="pi pi-refresh" label={t('video_wall.dish_ai.redo')} onClick={doEnhance} disabled={busy} />
                    <Button icon="pi pi-check" label={t('video_wall.dish_ai.use_photo')}
                      loading={save.isLoading} disabled={busy} onClick={() => persist(enhanced.token, file?.name)} />
                  </div>
                ) : (
                  <Button icon="pi pi-bolt" label={t('video_wall.dish_ai.enhance')} onClick={doEnhance} disabled={busy} />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
